// The routes and tracks open on the Plan map. Opening adds to what is already open; each keeps its colour
// until it is closed. Kept for the browser tab (sessionStorage), so moving between pages doesn't lose them.
import { useSyncExternalStore } from 'react';
import { isDraft } from './drafts';
import type { TrailKind } from './trail';

export interface TrailRef {
  kind: TrailKind;
  guid: string;
}

export interface OpenTrailsState {
  open: (TrailRef & { color: string })[];
  /** The trails to zoom to; `n` changes each time, so asking again re-zooms. */
  focus: { guids: string[]; n: number };
  /** The selected trail, drawn on top at full strength with the others faded; null shows all equally. */
  selected: string | null;
}

/**
 * Line colours for the first ten open trails, then repeating. Strong, clearly different colours, ordered so the
 * first few contrast most with each other and with what maps use for their own lines (roads orange and yellow,
 * long-distance paths red, water blue-grey, woods green); every line also has a white edge and dark outline.
 */
export const TRAIL_COLORS = [
  '#1f6bff', // blue
  '#ff7a00', // orange
  '#e5198a', // magenta
  '#6cc417', // lime
  '#8b3dff', // violet
  '#00b4d8', // cyan
  '#ffd23f', // yellow
  '#d7263d', // crimson
  '#1b2a6b', // navy
  '#00a676', // emerald
];

const STORAGE_KEY = 'xped.plan.open';
const empty: OpenTrailsState = { open: [], focus: { guids: [], n: 0 }, selected: null };

let state: OpenTrailsState = load();
const listeners = new Set<() => void>();

function load(): OpenTrailsState {
  try {
    const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? 'null') as OpenTrailsState['open'] | null;
    // Unsaved trails were in memory only, so they are gone after a reload.
    if (Array.isArray(saved))
      return {
        open: inPalette(saved.filter((o) => !isDraft(o.guid))),
        focus: { guids: [], n: 0 },
        selected: null,
      };
  } catch {
    // Private mode or bad data: start empty.
  }
  return empty;
}

function set(next: OpenTrailsState) {
  state = next;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next.open));
  } catch {
    // Not persisted; still works for this page.
  }
  for (const listener of listeners) listener();
}

/** The first colour not already in use (cycles when all are taken). */
function nextColor(used: string[]): string {
  return TRAIL_COLORS.find((c) => !used.includes(c)) ?? TRAIL_COLORS[used.length % TRAIL_COLORS.length]!;
}

/**
 * Colours remembered from earlier (this tab, or a recovered session) may be from an older palette or clash with
 * one in use: keep those that are current and free, and give the rest the next free colour.
 */
function inPalette(entries: OpenTrailsState['open'], open: OpenTrailsState['open'] = []) {
  const result = [...open];
  for (const entry of entries) {
    const used = result.map((o) => o.color);
    const keep = TRAIL_COLORS.includes(entry.color) && !used.includes(entry.color);
    result.push({ ...entry, color: keep ? entry.color : nextColor(used) });
  }
  return result.slice(open.length);
}

/** Adds trails (already-open ones stay as they are) and zooms to them; opening a single one selects it. */
export function openTrails(refs: TrailRef[]) {
  const open = [...state.open];
  for (const ref of refs) {
    if (open.some((o) => o.guid === ref.guid)) continue;
    open.push({ ...ref, color: nextColor(open.map((o) => o.color)) });
  }
  const selected = refs.length === 1 ? refs[0]!.guid : null;
  set({ open, focus: { guids: refs.map((r) => r.guid), n: state.focus.n + 1 }, selected });
}

/**
 * Closes a trail. If nothing was selected (everything shown) or it was the selected one, the map zooms to the
 * trails left; with another trail selected, the view stays on that one.
 */
export function closeTrail(guid: string) {
  const open = state.open.filter((o) => o.guid !== guid);
  const refit = state.selected === null || state.selected === guid;
  set({
    ...state,
    open,
    selected: state.selected === guid ? null : state.selected,
    focus: refit && open.length ? { guids: open.map((o) => o.guid), n: state.focus.n + 1 } : state.focus,
  });
}

export function closeAllTrails() {
  set({ ...state, open: [], selected: null });
}

/** Selects one open trail and zooms to it. */
export function selectTrail(guid: string) {
  set({ ...state, focus: { guids: [guid], n: state.focus.n + 1 }, selected: guid });
}

/**
 * Puts back trails recovered from an earlier session (draftStore), keeping their colours where they are free,
 * and zooms to them.
 */
export function restoreTrails(entries: OpenTrailsState['open']) {
  const fresh = entries.filter((e) => !state.open.some((o) => o.guid === e.guid));
  if (fresh.length === 0) return;
  const open = [...state.open, ...inPalette(fresh, state.open)];
  set({ ...state, open, focus: { guids: fresh.map((e) => e.guid), n: state.focus.n + 1 } });
}

/** A trail was saved under a new id: swap it in place, keeping its colour and selection. */
export function replaceTrail(oldGuid: string, ref: TrailRef) {
  set({
    ...state,
    open: state.open.map((o) => (o.guid === oldGuid ? { ...o, ...ref } : o)),
    selected: state.selected === oldGuid ? ref.guid : state.selected,
  });
}

/** Clears the selection: every open trail at full strength, and the map zooms to them all (as Show all). */
export function deselectTrail() {
  set({ ...state, focus: { guids: state.open.map((o) => o.guid), n: state.focus.n + 1 }, selected: null });
}

/** Show all: clears the selection and zooms to every open trail. */
export function showAllTrails() {
  set({ ...state, focus: { guids: state.open.map((o) => o.guid), n: state.focus.n + 1 }, selected: null });
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export const getOpenTrails = () => state;

export const subscribeOpenTrails = subscribe;

export function useOpenTrails(): OpenTrailsState {
  return useSyncExternalStore(subscribe, () => state);
}
